import { useEffect, useState, useRef, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import { deviceStore, type Device } from '@/stores/deviceStore';
import MainLayout from '@/components/layouts/MainLayout';
import { DeviceCard } from '@/components/dashboard/DeviceCard';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Search, Loader2, X } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { formatTime } from '@/utils/formatters';
import { motion, AnimatePresence } from 'framer-motion';

// Import Leaflet CSS in index.css or directly here
import 'leaflet/dist/leaflet.css';

// Add custom CSS for popups to ensure they appear above the map and all other UI elements
const customMapStyle = `
/* Increase stacking context for the entire map container */
.leaflet-container {
  position: relative;
  z-index: 0;
}

/* Force popups to appear above everything else, but below device control modal */
.leaflet-popup {
  position: absolute;
  z-index: 999 !important;
}

/* Ensure the control components have appropriate z-index */
.leaflet-control {
  z-index: 800 !important;
}

/* Make device control modal appear above map popups */
.fixed.inset-0.bg-black\\/50.flex.justify-center.items-center {
  z-index: 99990 !important;
}

.fixed.inset-0.bg-black\\/50.flex.justify-center.items-center > div {
  z-index: 99999 !important;
}

.fixed.inset-0.bg-black\\/50.flex.justify-center.items-center button {
  z-index: 100000 !important;
}

/* Prevent map interaction while control modal is open */
.map-interaction-disabled {
  pointer-events: none;
}

/* Device panel styles */
.device-panel {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 350px;
  background: white;
  z-index: 500;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  border-right: 1px solid #e5e7eb;
}

/* Responsive adjustments for small screens */
@media (max-width: 768px) {
  .device-panel {
    width: 100%;
    height: 75%;
    top: auto;
    bottom: 0;
    border-right: none;
    border-top: 1px solid #e5e7eb;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  }
}
`;

// Fix icon paths for leaflet
const fixLeafletIcons = () => {
  // Use alternative approach to fix icon paths
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: '/assets/marker-icon-2x.png',
    iconUrl: '/assets/marker-icon.png',
    shadowUrl: '/assets/marker-shadow.png',
  });
};

fixLeafletIcons();

// Extend the Device interface to include map coordinates
interface DeviceWithCoordinates extends Device {
  lat?: number | string;
  lng?: number | string;
}

// Create custom icons for devices
const createDeviceIcon = (isOn: boolean, isOnline: boolean, isSelected: boolean) => {
  // Choose colors based on device status and selection state
  const bgColor = isOnline 
    ? (isOn ? 'bg-yellow-400' : 'bg-white')
    : 'bg-white';
    
  const borderColor = isOnline
    ? (isOn ? 'border-yellow-500' : 'border-green-500')
    : 'border-gray-400';
    
  const iconColor = isOnline
    ? (isOn ? 'text-yellow-700' : 'text-green-500')
    : 'text-gray-400';
    
  const scale = isSelected ? 'scale-125' : 'scale-100';
  const zIndex = isSelected ? 'z-10' : 'z-0';
  
  return L.divIcon({
    className: `custom-device-icon ${zIndex}`,
    html: `
      <div class="w-10 h-10 rounded-full flex items-center justify-center ${bgColor} border-2 ${borderColor} ${scale} transition-transform duration-300">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 ${iconColor}" viewBox="0 0 24 24" stroke="currentColor" fill="none" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
  });
};

// Component to handle map operations like fitting bounds
const MapController = observer(({ 
  isControlActive,
  selectedDeviceId,
  onMapClick
}: { 
  isControlActive: boolean,
  selectedDeviceId: string | null,
  onMapClick: () => void
}) => {
  const map = useMap();
  const { devices } = deviceStore;
  
  // Handle map click to close device panel
  useEffect(() => {
    if (!isControlActive) {
      map.on('click', (e) => {
        // The click event is triggered on the map itself, not on markers
        onMapClick();
      });

      return () => {
        map.off('click');
      };
    }
  }, [map, isControlActive, onMapClick]);

  useEffect(() => {
    if (devices.length > 0 && !isControlActive) {
      // Filter devices that have valid coordinates
      const validDevices = devices.filter(device => {
        const d = device as DeviceWithCoordinates;
        return d.lat && d.lng && 
          !Number.isNaN(Number.parseFloat(String(d.lat))) && 
          !Number.isNaN(Number.parseFloat(String(d.lng)));
      });
      
      if (validDevices.length > 0) {
        const bounds = L.latLngBounds(
          validDevices.map(device => {
            const d = device as DeviceWithCoordinates;
            return [
              Number.parseFloat(String(d.lat)), 
              Number.parseFloat(String(d.lng))
            ];
          })
        );
        
        // If a device is selected, don't fit to all bounds - let user explore
        if (!selectedDeviceId) {
          map.fitBounds(bounds, { padding: [50, 50] });
        }
      }
    }
    
    // Toggle map interaction based on control state
    if (isControlActive) {
      map.getContainer().classList.add('map-interaction-disabled');
      // Disable dragging and other interactions
      map.dragging.disable();
      map.touchZoom.disable();
      map.doubleClickZoom.disable();
      map.scrollWheelZoom.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
    } else {
      map.getContainer().classList.remove('map-interaction-disabled');
      // Re-enable map interactions
      map.dragging.enable();
      map.touchZoom.enable();
      map.doubleClickZoom.enable();
      map.scrollWheelZoom.enable();
      map.boxZoom.enable();
      map.keyboard.enable();
    }
  }, [devices, map, isControlActive, selectedDeviceId]);

  // Center the map on the selected device when it changes
  useEffect(() => {
    if (selectedDeviceId && !isControlActive) {
      const selectedDevice = devices.find(device => device.device_id === selectedDeviceId) as DeviceWithCoordinates;
      if (selectedDevice?.lat && selectedDevice?.lng) {
        const lat = Number.parseFloat(String(selectedDevice.lat));
        const lng = Number.parseFloat(String(selectedDevice.lng));
        
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          // Fly to the selected device with animation
          map.flyTo([lat, lng], map.getZoom() < 14 ? 14 : map.getZoom(), {
            duration: 0.8
          });
        }
      }
    }
  }, [selectedDeviceId, devices, map, isControlActive]);
  
  return null;
});

// Check if a device is online based on its last update time
const isDeviceOnline = (device: Device): boolean => {
  const status = deviceStore.deviceStatus[device.device_id] || {};
  const s = status.s || {};
  
  if (!s.lt && !s.time) return false;
  
  const lastUpdateTime = new Date(s.lt || s.time || '').getTime();
  const now = Date.now();
  
  // Device is considered online if the last update was within the last 15 minutes
  return now - lastUpdateTime < 15 * 60 * 1000;
};

// Check if a device relay is in ON state
const isDeviceOn = (device: Device): boolean => {
  const status = deviceStore.deviceStatus[device.device_id] || {};
  const s = status.s || {};
  const relay = device.relay;
  
  if (!relay || relay < 1 || relay > 3) return false;
  
  // Check if the relay state is 1 (ON)
  const relayState = s[`r${relay}` as keyof typeof s];
  return relayState === 1;
};

const DeviceMapPage = observer(() => {
  const { t } = useTranslation();
  const { devices, loading, error } = deviceStore;
  const [searchTerm, setSearchTerm] = useState('');
  const [mapReady, setMapReady] = useState(false);
  const [isControlActive, setIsControlActive] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Inject custom map styles
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = customMapStyle;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Handle "Control Device" button clicks and modals
  const handleControlStart = useCallback(() => {
    setIsControlActive(true);
  }, []);
  
  const handleControlEnd = useCallback(() => {
    setIsControlActive(false);
  }, []);
  
  // Listen for device control events from DeviceCard
  useEffect(() => {
    const startControlHandler = () => handleControlStart();
    const endControlHandler = () => handleControlEnd();
    
    // Add event listeners
    window.addEventListener('device-control-start', startControlHandler);
    window.addEventListener('device-control-end', endControlHandler);
    
    return () => {
      // Clean up event listeners
      window.removeEventListener('device-control-start', startControlHandler);
      window.removeEventListener('device-control-end', endControlHandler);
    };
  }, [handleControlStart, handleControlEnd]);
  
  // Filter devices based on search term
  const filteredDevices = devices.filter(device => {
    if (!searchTerm.trim()) return true;
    
    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    
    return (
      (device.device_id || '').toLowerCase().includes(lowerSearchTerm) ||
      (device.pillarno || '').toString().toLowerCase().includes(lowerSearchTerm) ||
      (device.area || '').toLowerCase().includes(lowerSearchTerm) ||
      (device.point || '').toLowerCase().includes(lowerSearchTerm)
    );
  });
  
  // Get devices with valid coordinates for the map
  const devicesWithCoordinates = filteredDevices.filter(device => {
    const d = device as DeviceWithCoordinates;
    return d.lat && d.lng && 
      !Number.isNaN(Number.parseFloat(String(d.lat))) && 
      !Number.isNaN(Number.parseFloat(String(d.lng)));
  });
  
  // Find the selected device
  const selectedDevice = selectedDeviceId 
    ? devices.find(device => device.device_id === selectedDeviceId) 
    : null;
  
  // Count devices by status
  const onlineDevicesCount = devices.filter(d => isDeviceOnline(d)).length;
  const onDevicesCount = devices.filter(d => isDeviceOnline(d) && isDeviceOn(d)).length;
  const offDevicesCount = onlineDevicesCount - onDevicesCount; // Online but OFF
  const offlineDevicesCount = devices.length - onlineDevicesCount;
  
  // Fetch devices when component mounts
  useEffect(() => {
    // Call loadDevices directly on deviceStore instead of using destructured method
    deviceStore.loadDevices();
    
    // Create a polling interval to keep device status updated
    const intervalId = setInterval(() => {
      deviceStore.loadDevices();
    }, 60000); // Update every minute
    
    return () => clearInterval(intervalId);
  }, []);
  
  // Set default map position
  const defaultPosition: [number, number] = [38.2466, 21.7346]; // Default position (e.g., Patras, Greece)
  
  // Handle map ready event
  const handleMapReady = () => {
    setMapReady(true);
  };

  // Handle map click (not on marker)
  const handleMapClick = useCallback(() => {
    if (selectedDeviceId) {
      setSelectedDeviceId(null);
    }
  }, [selectedDeviceId]);
  
  return (
    <MainLayout>
      <div className="space-y-6">
        <Card className="p-6">
          <h1 className="text-2xl font-bold mb-2">{t('deviceMap.title', 'Device Map')}</h1>
          <p className="text-gray-600 mb-4">{t('deviceMap.subtitle', 'View and manage your devices on the map')}</p>
          
          <div className="relative mb-4">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder={t('dashboard.searchDevices')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
          
          {loading && (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          )}
          
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {!loading && !error && (
            <>
              <div className="mb-4">
                <div className="flex items-center gap-4 mb-2 flex-wrap">
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-yellow-400 border border-yellow-500 mr-2" />
                    <span>{t('deviceMap.onDevices', 'ON')}: {onDevicesCount}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-white border border-green-500 mr-2" />
                    <span>{t('deviceMap.offDevices', 'OFF')}: {offDevicesCount}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-white border border-gray-400 mr-2" />
                    <span>{t('deviceMap.offlineDevices', 'Offline')}: {offlineDevicesCount}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-white border border-blue-500 mr-2" />
                    <span>{t('deviceMap.onlineDevices', 'Online')}: {onlineDevicesCount}</span>
                  </div>
                  <div className="flex items-center ml-auto">
                    <span>{t('deviceMap.totalDevices', 'Total')}: {devices.length}</span>
                  </div>
                </div>
                
                <div className="text-sm text-gray-500">
                  {t('deviceMap.lastUpdated', 'Last updated')}: {formatTime(new Date().toISOString())}
                </div>
              </div>
              
              <div 
                ref={mapContainerRef}
                className={`h-[calc(100vh-320px)] ${isMobile ? 'min-h-[700px]' : 'min-h-[600px]'} relative rounded-md overflow-hidden border ${isControlActive ? 'disable-popup-autopan' : ''} ${selectedDeviceId ? (isMobile ? 'pb-[75%]' : 'pl-[350px]') : ''}`}
              >
                {/* Device Details Panel */}
                <AnimatePresence>
                  {selectedDevice && (
                    <motion.div 
                      className="device-panel p-4"
                      initial={{ x: isMobile ? 0 : -350, y: isMobile ? 300 : 0 }}
                      animate={{ x: 0, y: 0 }}
                      exit={{ x: isMobile ? 0 : -350, y: isMobile ? 300 : 0 }}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="text-lg font-semibold">{t('deviceMap.controlDevice', 'Device Details')}</h3>
                        <button 
                          type="button"
                          onClick={handleMapClick}
                          className="p-1 rounded-full hover:bg-gray-100"
                          aria-label="Close panel"
                        >
                          <X className="h-5 w-5 text-gray-500" />
                        </button>
                      </div>
                      <DeviceCard 
                        device={selectedDevice} 
                        mapView={true}
                        onControlStart={handleControlStart}
                        onControlEnd={handleControlEnd}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
                
                <MapContainer
                  center={defaultPosition}
                  zoom={12}
                  style={{ height: '100%', width: '100%' }}
                  whenReady={handleMapReady}
                  className={`map-container ${isControlActive ? 'control-active' : ''}`}
                  doubleClickZoom={!isControlActive}
                  dragging={!isControlActive}
                  zoomControl={!isControlActive}
                >
                  <TileLayer
                    attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  
                  {mapReady && (
                    <MapController 
                      isControlActive={isControlActive} 
                      selectedDeviceId={selectedDeviceId}
                      onMapClick={handleMapClick}
                    />
                  )}
                  
                  {devicesWithCoordinates.map(device => {
                    const d = device as DeviceWithCoordinates;
                    const deviceIsOnline = isDeviceOnline(device);
                    const deviceIsOn = isDeviceOnline(device) && isDeviceOn(device);
                    const isSelected = selectedDeviceId === device.device_id;
                    
                    return (
                      <Marker
                        key={device.device_id}
                        position={[
                          Number.parseFloat(String(d.lat)), 
                          Number.parseFloat(String(d.lng))
                        ]}
                        icon={createDeviceIcon(deviceIsOn, deviceIsOnline, isSelected)}
                        eventHandlers={{
                          click: (e) => {
                            e.originalEvent.stopPropagation();
                            setSelectedDeviceId(device.device_id);
                          }
                        }}
                      />
                    );
                  })}
                </MapContainer>
              </div>
              
              {devicesWithCoordinates.length === 0 && !loading && (
                <div className="text-center p-4 bg-gray-100 rounded-md my-4">
                  {searchTerm ? 
                    t('deviceMap.noDevicesFoundForSearch', 'No devices matching your search') :
                    t('deviceMap.noDevicesWithCoordinates', 'No devices with location data available')}
                </div>
              )}
              
              {filteredDevices.length > devicesWithCoordinates.length && (
                <div className="text-sm text-gray-500 mt-2">
                  {t('deviceMap.devicesWithoutCoordinates', '{{count}} devices do not have valid coordinates and are not shown on the map', { count: filteredDevices.length - devicesWithCoordinates.length })}
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </MainLayout>
  );
});

export default DeviceMapPage; 